import { memo, useCallback, useMemo, useState } from 'react'

import { Speed as SpeedIcon, WifiOff as WifiOffIcon } from '@mui/icons-material'
import { Box, Button, CircularProgress, Paper, Skeleton, Tooltip, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { formatSpeedtestData, speedtestDone } from 'domains/ContentProcessing/AvcInstances/Instance/Dashboard'
import { avcInstanceQueryKeys } from 'services/queryKeys'

type SpeedtestItemProps = {
  inProgress: boolean
  label: string
  skeletonWidth?: number
  value?: string | number
  fallback: string
}

const SpeedtestItem = ({ inProgress, label, value, skeletonWidth = 50, fallback }: SpeedtestItemProps) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography>{label}:</Typography>
    {value ? (
      <Typography>{value}</Typography>
    ) : inProgress ? (
      <Skeleton variant="text" style={{ width: skeletonWidth }} />
    ) : (
      fallback
    )}
  </Box>
)

type NetworkingProps = {
  instanceId: string
}

const NetworkingRaw = ({ instanceId }: NetworkingProps) => {
  const { t } = useTranslation('domain')

  const [disabled, setDisabled] = useState(false)

  const { data: speedtest, refetch } = useQuery({
    ...avcInstanceQueryKeys.speedtestGet({ instanceId }),
    // refetch every 5 seconds until state is Completed, Failed or Stopped
    refetchInterval(query) {
      if (query.state.data?.state ? speedtestDone.includes(query.state.data?.state) : true) {
        return false
      }
      return 5000
    },
  })

  const { refetch: startSpeedtest } = useQuery({
    ...avcInstanceQueryKeys.speedtestStart({ instanceId }),
    enabled: false,
  })

  const { refetch: stopSpeedtest } = useQuery({
    ...avcInstanceQueryKeys.speedtestStop({ instanceId }),
    enabled: false,
  })

  const data = useMemo(() => formatSpeedtestData(speedtest), [speedtest])

  const onStartSpeedTest = useCallback(async () => {
    setDisabled(true)
    await startSpeedtest()
    await refetch()
    setDisabled(false)
  }, [startSpeedtest, refetch])

  const onStopSpeedTest = useCallback(async () => {
    setDisabled(true)
    await stopSpeedtest()
    await refetch()
    setDisabled(false)
  }, [stopSpeedtest, refetch])

  const inProgressLabel = useMemo(() => {
    switch (speedtest?.state) {
      case 'STARTING':
        return t('ContentProcessing.Instance.starting')
      case 'STOPPING':
        return t('ContentProcessing.Instance.stopping')
      default:
        return t('ContentProcessing.Instance.inProgress')
    }
  }, [t, speedtest?.state])

  const buttonDisabled = useMemo(
    () => disabled || speedtest?.state === 'STOPPING' || speedtest?.state === 'STARTING',
    [speedtest, disabled],
  )

  return (
    <Paper sx={{ p: 2, maxWidth: 400 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="heading-md">{t('ContentProcessing.Instance.lastSpeedtest')}</Typography>
        <SpeedIcon />
      </Box>
      <Typography sx={{ display: 'flex', alignItems: 'center', height: 21, mt: 0.5, mb: 2 }}>
        {data.speedtestInProgress ? (
          <>
            {inProgressLabel}...
            <CircularProgress sx={{ ml: 1 }} size={15} />
          </>
        ) : (
          data?.timestamp && formatDistanceToNow(new Date(data.timestamp * 1000), { addSuffix: true })
        )}
      </Typography>
      {data.speedtestInProgress || data.download || data.upload || data.ping || data.serverInformation?.countryName ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <SpeedtestItem
            label={t('ContentProcessing.Instance.upload')}
            value={data.upload}
            skeletonWidth={90}
            fallback="N/A"
            inProgress={data.speedtestInProgress}
          />
          <SpeedtestItem
            label={t('ContentProcessing.Instance.download')}
            value={data.download}
            skeletonWidth={90}
            fallback="N/A"
            inProgress={data.speedtestInProgress}
          />
          <SpeedtestItem
            label={t('ContentProcessing.Instance.ping')}
            value={data.ping}
            skeletonWidth={60}
            fallback="N/A"
            inProgress={data.speedtestInProgress}
          />
          <Tooltip
            leaveDelay={200}
            placement="right"
            title={
              !data.clientInformation && !data.serverInformation ? null : (
                <Box sx={{ display: 'flex', flexDirection: 'column', p: 1.5 }}>
                  <Typography sx={{ mb: 1 }}>{t('ContentProcessing.Instance.clientInformation')}:</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, auto)',
                      gridAutoRows: 'max-content, auto',
                      justifyItems: 'flex-end',
                      columnGap: 3,
                    }}
                  >
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.ipAddress')}:
                    </Typography>
                    <Typography variant="text-sm">{data.clientInformation?.ipAddress || 'N/A'}</Typography>
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.ispName')}:
                    </Typography>
                    <Typography variant="text-sm">{data.clientInformation?.ispName || 'N/A'}</Typography>
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.longitude')}:
                    </Typography>
                    <Typography variant="text-sm">{data.clientInformation?.location?.longitude || 'N/A'}</Typography>
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.latitude')}:
                    </Typography>
                    <Typography variant="text-sm">{data.clientInformation?.location?.latitude || 'N/A'}</Typography>
                  </Box>

                  <Typography sx={{ mt: 2, mb: 1 }}>{t('ContentProcessing.Instance.serverInformation')}:</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, auto)',
                      gridAutoRows: 'max-content, auto',
                      justifyItems: 'flex-end',
                      columnGap: 3,
                    }}
                  >
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.countryName')}:
                    </Typography>
                    <Typography variant="text-sm">{data.serverInformation?.countryName || 'N/A'}</Typography>
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.ipAddress')}:
                    </Typography>
                    <Typography variant="text-sm">{data.serverInformation?.ipAddress || 'N/A'}</Typography>
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.name')}:
                    </Typography>
                    <Typography variant="text-sm">{data.serverInformation?.name || 'N/A'}</Typography>
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.providerName')}:
                    </Typography>
                    <Typography variant="text-sm">{data.serverInformation?.providerName || 'N/A'}</Typography>
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.longitude')}:
                    </Typography>
                    <Typography variant="text-sm">{data.serverInformation?.location?.longitude || 'N/A'}</Typography>
                    <Typography variant="text-sm" sx={{ justifySelf: 'flex-start' }}>
                      {t('ContentProcessing.Instance.latitude')}:
                    </Typography>
                    <Typography variant="text-sm">{data.serverInformation?.location?.latitude || 'N/A'}</Typography>
                  </Box>
                </Box>
              )
            }
          >
            <Box sx={{ cursor: !data.clientInformation && !data.serverInformation ? 'auto' : 'help' }}>
              <SpeedtestItem
                label={t('ContentProcessing.Instance.location')}
                value={data.serverInformation?.countryName || undefined}
                skeletonWidth={70}
                fallback={t('ContentProcessing.Instance.unknown')}
                inProgress={data.speedtestInProgress}
              />
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <Box
          sx={{
            // to match the height of box with data so there is no shifts in component layout
            minHeight: 108,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <WifiOffIcon />
          <Typography variant="heading-md">{t('ContentProcessing.Instance.noDataTitle')}</Typography>
          <Typography sx={{ textAlign: 'center' }}>{t('ContentProcessing.Instance.noDataText')}</Typography>
        </Box>
      )}
      <Button
        sx={{ mt: 3, width: '100%' }}
        disabled={buttonDisabled}
        onClick={data.speedtestInProgress ? onStopSpeedTest : onStartSpeedTest}
        variant={data.speedtestInProgress ? 'contained' : 'outlined'}
      >
        {data.speedtestInProgress
          ? t('ContentProcessing.Instance.stopSpeedtest')
          : t('ContentProcessing.Instance.startSpeedtest')}
      </Button>
    </Paper>
  )
}

export const Networking = memo(NetworkingRaw)
